// Video library utility functions

export type SortField = 'name' | 'date' | 'size' | 'duration'
export type SortOrder = 'asc' | 'desc'
export type ViewMode = 'grid' | 'list'

export interface VideoFilters {
  search: string
  sortField: SortField
  sortOrder: SortOrder
  formats: string[]
  viewMode: ViewMode
}

// Utility function to sort videos
export function sortVideos<T extends { name: string; uploadDate: string; size: number; duration?: string }>(
  videos: T[],
  sortField: SortField,
  sortOrder: SortOrder
): T[] {
  return [...videos].sort((a, b) => {
    let comparison = 0

    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name)
        break
      case 'date':
        comparison = new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime()
        break
      case 'size':
        comparison = a.size - b.size
        break
      case 'duration': {
        // Parse duration string (MM:SS or HH:MM:SS)
        const parseDuration = (d?: string) => {
          if (!d) return 0
          const parts = d.split(':').map(Number)
          if (parts.length === 2) return parts[0] * 60 + parts[1]
          if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
          return 0
        }
        comparison = parseDuration(a.duration) - parseDuration(b.duration)
        break
      }
    }

    return sortOrder === 'asc' ? comparison : -comparison
  })
}

// Utility function to filter videos
export function filterVideos<T extends { name: string; mimeType?: string }>(
  videos: T[],
  filters: VideoFilters
): T[] {
  return videos.filter((video) => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      if (!video.name.toLowerCase().includes(searchLower)) {
        return false
      }
    }

    // Format filter
    if (filters.formats.length > 0) {
      const videoFormat = video.mimeType?.split('/')[1]?.toUpperCase() || ''
      if (!filters.formats.includes(videoFormat)) {
        return false
      }
    }

    return true
  })
}
